import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../../sharedComponents/Typography/Typography';
import { TransferToContainerProps } from './TransferToContainer.types';
import { ACHMaintenanceNotice, AddPayoutAccountButton } from '../../components';
import {
  achProviderMaintenance,
  additionalPayoutProviderSupportsCurrency,
  alternatePayoutAccountSupportsCurrency,
  payoutAccountSupportsCurrency,
  paypalProviderMaintenance,
} from '../../utils/payoutAccountHelpers';
import { ConfigContext } from '../../contexts';
import { useAppSelector } from '../../store/hooks';
import { selectors } from '../../store/store';
import styles from './transfer-to-container.module.scss';
import {
  PayoutAccount,
  PayoutProvider,
  PayoutProviderDisplay,
} from '../../utils/types/Entity';
import Modal from '../../sharedComponents/Modal/Modal';
import Button from '../../components/Button/Button';
import UnsupportedCurrencyNotice from '../../components/UnsupportedCurrencyNotice/UnsupportedCurrencyNotice';
import PayPalMaintenanceNotice from '../../components/PaypalMaintenanceNotice/PayPalMaintenanceNotice';

const TransferToContainer: React.FunctionComponent<
  TransferToContainerProps
> = ({
  currency,
  goToChangeAccountStep,
  approveClaims,
  approveClaimsError,
  processingRequest,
  bundleID,
}) => {
  const { t } = useTranslation();
  const [renderDefault, setRenderDefault] = useState(false);
  const [validAccountsForCurrency, setValidAccountsForCurrency] = useState<
    PayoutAccount[]
  >([]);
  const [renderChoose, setRenderChoose] = useState(false);
  const [renderAdd, setRenderAdd] = useState(false);
  const [modalActive, setModalActive] = useState(false);
  const [accountToTransfer, setAccountToTransfer] = useState<PayoutAccount>();
  const configEnvelope = useContext(ConfigContext);

  const appState = useAppSelector((state) => state);
  const {
    payoutAccount: { payoutAccounts },
  } = appState;

  const defaultAccount = selectors.defaultPayoutAccount(appState);
  const achMaintenance = configEnvelope.config?.dwollaDisabled;
  const paypalMaintenance = configEnvelope.config?.paypalDisabled;

  useEffect(() => {
    if (defaultAccount) {
      const render = payoutAccountSupportsCurrency(defaultAccount, currency);
      setRenderDefault(render);
      setValidAccountsForCurrency([]);
    } else {
      const validAccounts = payoutAccounts.filter((account) =>
        payoutAccountSupportsCurrency(account, currency),
      );
      setValidAccountsForCurrency(validAccounts);
      setRenderDefault(validAccounts.length > 0);
    }
  }, [currency, payoutAccounts, defaultAccount]);

  useEffect(() => {
    const render = alternatePayoutAccountSupportsCurrency(
      payoutAccounts,
      currency,
    );
    setRenderChoose(render);
  }, [currency, payoutAccounts]);

  useEffect(() => {
    const render = additionalPayoutProviderSupportsCurrency(
      payoutAccounts,
      currency,
    );
    setRenderAdd(render);
  }, [currency, payoutAccounts]);

  const providerEligibilityMap = useAppSelector(
    (state) =>
      state.policyBundles.policyBundles.find((bundle) => bundle.id === bundleID)
        ?.eligibilityMap,
  );

  const normalizedProviderName =
    defaultAccount?.providerName === 'PayPal'
      ? PayoutProviderDisplay.PayPal
      : PayoutProviderDisplay.Bank;

  let defaultAccountButton: JSX.Element | null = null;

  if (!defaultAccount && validAccountsForCurrency.length > 0) {
    defaultAccountButton = (
      <>
        {validAccountsForCurrency.map((account) => {
          const providerEligible =
            providerEligibilityMap?.[account.providerName];
          const provider =
            account.providerName === 'PayPal'
              ? PayoutProviderDisplay.PayPal
              : PayoutProviderDisplay.Bank;
          //TODO: remove this paypal specific logic when ready to validate all providers' eligibility. replace with
          // general provider maintenance component
          const showPayPalEligibilityNotice =
            account.providerName === PayoutProvider.PayPal &&
            providerEligible === false;
          return showPayPalEligibilityNotice ? (
            <PayPalMaintenanceNotice key={account.id} />
          ) : (
            <AddPayoutAccountButton
              key={account.id}
              providerLabel
              provider={provider}
              displayName={account.displayName}
              clickHandler={() => {
                setAccountToTransfer(account);
                setModalActive(true);
              }}
              colorVariant="ocean"
              error={approveClaimsError}
              disabled={processingRequest}
              isDefault={account.isDefault}
            />
          );
        })}
      </>
    );
  } else if (defaultAccount) {
    const defaultProviderEligible =
      providerEligibilityMap?.[defaultAccount.providerName];
    //TODO: remove this paypal specific logic when ready to validate all providers' eligibility. replace with
    // general provider maintenance component
    const showPayPalEligibilityNotice =
      defaultAccount.providerName === PayoutProvider.PayPal &&
      defaultProviderEligible === false;
    defaultAccountButton = showPayPalEligibilityNotice ? (
      <PayPalMaintenanceNotice key={defaultAccount.id} />
    ) : (
      <AddPayoutAccountButton
        providerLabel
        provider={normalizedProviderName}
        displayName={defaultAccount.displayName}
        clickHandler={() => {
          setAccountToTransfer(defaultAccount);
          setModalActive(true);
        }}
        colorVariant="ocean"
        error={approveClaimsError}
        disabled={processingRequest}
        isDefault={true}
      />
    );
  }

  const chooseAnotherAccountButton = (
    <AddPayoutAccountButton
      chooseAnotherAccountLabel
      clickHandler={goToChangeAccountStep}
      colorVariant="ocean"
      disabled={processingRequest}
    />
  );

  const addPayoutAccountButton = (
    <AddPayoutAccountButton
      addNewAccountLabel
      clickHandler={goToChangeAccountStep}
      colorVariant="ocean"
      disabled={processingRequest}
    />
  );

  const defaultAccountDoesNotSupportCurrency =
    defaultAccount && !payoutAccountSupportsCurrency(defaultAccount, currency);
  //TODO: refactor to determine the maintenance status of any default account
  const defaultACHAccountMaintenance = achProviderMaintenance(
    defaultAccount,
    achMaintenance,
  );

  const defaultPayPalAccountMaintenance = paypalProviderMaintenance(
    defaultAccount,
    paypalMaintenance,
  );

  return (
    <div>
      <Typography variant="h5">
        {t('sunshine.content.reimbursement_balance.transfer_to')}
      </Typography>
      {achMaintenance && <ACHMaintenanceNotice />}
      {paypalMaintenance && <PayPalMaintenanceNotice />}
      {!achMaintenance &&
        !paypalMaintenance &&
        defaultAccountDoesNotSupportCurrency && (
          <UnsupportedCurrencyNotice
            payoutAccount={defaultAccount}
            policyCurrency={currency}
          />
        )}

      {renderDefault &&
        !defaultACHAccountMaintenance &&
        !defaultPayPalAccountMaintenance &&
        defaultAccountButton}

      {renderChoose && chooseAnotherAccountButton}

      {!renderChoose && renderAdd && addPayoutAccountButton}
      {accountToTransfer && (
        <Modal
          isActive={modalActive}
          onCloseHandler={() => setModalActive(false)}
        >
          <div className={styles.confirmationContainer}>
            <Typography variant="h5">
              {t('sunshine.content.want_to_continue_transfer')}
            </Typography>
            <Typography variant="body-2">
              {t('sunshine.content.transferring_to')}{' '}
              {accountToTransfer.displayName}
            </Typography>
            <div className={styles.btnWrapper}>
              <Button
                label={t('label.yes')}
                colorVariant="ocean"
                clickHandling={() => {
                  approveClaims(accountToTransfer.id);
                  setModalActive(false);
                }}
                errorMsg={''}
              />
              <Button
                label={t('label.no')}
                colorVariant="ocean"
                clickHandling={() => setModalActive(false)}
                errorMsg={''}
              />
            </div>
          </div>
        </Modal>
      )}
      <div></div>
    </div>
  );
};

export default TransferToContainer;
